import { Helmet } from "react-helmet";
import { useState, useCallback, useMemo, useEffect } from "react";
import {
  FaChevronRight, FaChevronLeft, FaAnglesLeft, FaAnglesRight,
  FaCreditCard, FaHashtag, FaMagnifyingGlass, FaFilter, FaUser,
  FaFileInvoiceDollar, FaPlus, FaWhatsapp, FaEye, FaPaperPlane, FaXmark,
  FaMoneyBill, FaHandshake, // Icon baru untuk Janji Bayar
} from "react-icons/fa6";
import { FaCheckCircle, FaTimesCircle, FaSpinner } from "react-icons/fa";
import { formatRupiah } from "../../../../utilities/formatRupiah";
import { BtnDefault } from "../../../../components/buttons/BtnDefault";
import { Header } from "../../../../components/Header";
import { DashboardLayout } from "../../../../components/layouts/DashboardLayout";
import { useHookInvoice } from "../../../../hooks/useHookInvoice";
import { ModalInvoiceCreate } from "./Create";
import { InvoiceService } from "../../../../services/InvoiceService";
import { ModalInvoicePayment } from "./Payment";
import { CreateNewPromiseToPayModal } from "../promise_to_pay/modals/create";
// Import Modal Janji Bayar (Pastikan Anda membuat file ini)

/* ============================================
   Skeleton
   ============================================ */
const SkeletonRow = ({ cols = 6 }) => (
  <tr className="animate-pulse">
    {Array.from({ length: cols }).map((_, i) => (
      <td key={i} className="px-4 py-3">
        <div className="h-3.5 bg-slate-700/60 rounded-md" style={{ width: `${40 + Math.random() * 50}%` }} />
      </td>
    ))}
  </tr>
);

const SkeletonCard = () => (
  <div className="rounded-2xl overflow-hidden animate-pulse" style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}>
    <div className="px-4 py-3 flex justify-between items-center" style={{ background: "rgba(15, 23, 42, 0.7)", borderBottom: "1px solid rgba(51, 65, 85, 0.2)" }}>
      <div className="flex items-center gap-2"><div className="w-7 h-7 rounded-lg bg-slate-700/60" /><div className="h-3 bg-slate-700/50 rounded-md w-20" /></div>
      <div className="h-6 bg-slate-700/40 rounded-full w-14" />
    </div>
    <div className="p-4">
      <div className="flex justify-between items-center mb-3">
        <div className="space-y-2"><div className="h-5 bg-slate-700/60 rounded-md w-32" /><div className="h-3 bg-slate-700/30 rounded-md w-24" /></div>
        <div className="text-right space-y-1"><div className="h-2.5 bg-slate-700/30 rounded w-10 ml-auto" /><div className="h-6 bg-slate-700/50 rounded-md w-24 ml-auto" /></div>
      </div>
    </div>
  </div>
);

/* ============================================
   Status Badge
   ============================================ */
   const StatusBadge = ({ status }) => {
    // Normalisasi status ke huruf kecil agar lebih akurat dalam pengecekan
    const s = (status || "").toLowerCase();
  
    // Tentukan warna badge berdasarkan status
    let styleClass = "";
    let dotColorClass = "";
  
    if (s === 'lunas' || s === 'paid') {
      // Status: LUNAS (Hijau)
      styleClass = "bg-emerald-500/12 text-emerald-400 border border-emerald-500/20";
      dotColorClass = "bg-emerald-400";
    } else if (s === 'dibayar sebagian' || s === 'sebagian') {
      // Status: SEBAGIAN (Kuning/Amber)
      styleClass = "bg-amber-500/12 text-amber-400 border border-amber-500/20";
      dotColorClass = "bg-amber-400";
    } else {
      // Status: BELUM DIBAYAR (Merah) - Default
      styleClass = "bg-red-500/12 text-red-400 border border-red-500/20";
      dotColorClass = "bg-red-400";
    }
  
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold tracking-wide ${styleClass}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${dotColorClass}`} />
        {status || "-"}
      </span>
    );
  };

/* ============================================
   Toast
   ============================================ */
const Toast = ({ message, type, onClose }) => {
  const ok = type === "success";
  return (
    <div className={`fixed top-6 right-6 z-[100] flex items-start gap-3 px-5 py-4 rounded-xl shadow-2xl max-w-md border transition-all duration-300 ${ok ? "bg-emerald-950/90 border-emerald-500/30" : "bg-red-950/90 border-red-500/30"}`} style={{ backdropFilter: "blur(16px)" }}>
      {ok ? <FaCheckCircle className="text-emerald-400 text-lg mt-0.5 flex-shrink-0" /> : <FaTimesCircle className="text-red-400 text-lg mt-0.5 flex-shrink-0" />}
      <p className={`text-sm font-medium ${ok ? "text-emerald-200" : "text-red-200"}`}>{message}</p>
      <button onClick={onClose} className={`flex-shrink-0 ml-2 ${ok ? "text-emerald-400/60 hover:text-emerald-300" : "text-red-400/60 hover:text-red-300"}`}><FaXmark className="text-sm" /></button>
    </div>
  );
};

/* ============================================
   Modal Preview
   ============================================ */
const ModalPreview = ({ open, onClose, data, loading }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg rounded-2xl overflow-hidden shadow-2xl" style={{ background: "rgba(15, 23, 42, 0.95)", border: "1px solid rgba(51, 65, 85, 0.4)" }} onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: "1px solid rgba(51, 65, 85, 0.3)" }}>
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "rgba(0, 180, 216, 0.12)", border: "1px solid rgba(0, 180, 216, 0.2)" }}><FaEye className="text-sm text-cyan-400" /></div>
            <div><h3 className="text-sm font-bold text-gray-100">Preview Pesan</h3><p className="text-xs text-slate-500">{loading ? "Memuat..." : data?.name || "-"}</p></div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-gray-100 hover:bg-slate-700/50 transition-all"><FaXmark className="text-sm" /></button>
        </div>
        <div className="p-5 max-h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="space-y-3 animate-pulse">{[...Array(8)].map((_, i) => <div key={i} className="h-4 bg-slate-700/40 rounded" style={{ width: `${60 + Math.random() * 35}%` }} />)}</div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 mb-4 p-3 rounded-xl" style={{ background: "rgba(51, 65, 85, 0.15)", border: "1px solid rgba(51, 65, 85, 0.2)" }}>
                <div><p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">No. HP</p><p className="text-sm font-medium text-gray-200 mt-0.5">{data?.phone_number || "-"}</p></div>
                <div><p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Paket</p><p className="text-sm font-medium text-gray-200 mt-0.5">{data?.package || "-"}</p></div>
              </div>
              <pre className="whitespace-pre-wrap text-xs leading-relaxed text-slate-300 font-sans p-4 rounded-xl" style={{ background: "rgba(30, 41, 59, 0.6)", border: "1px solid rgba(51, 65, 85, 0.25)" }}>{data?.message || "-"}</pre>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* ============================================
   Modal Konfirmasi Kirim
   ============================================ */
const ModalSendConfirm = ({ open, onClose, onConfirm, loading, count }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="relative w-full max-w-sm rounded-2xl overflow-hidden shadow-2xl" style={{ background: "rgba(15, 23, 42, 0.95)", border: "1px solid rgba(51, 65, 85, 0.4)" }} onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: "1px solid rgba(51, 65, 85, 0.3)" }}>
          <h3 className="text-sm font-bold text-gray-100">Kirim Invoice</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-gray-100 hover:bg-slate-700/50 transition-all"><FaXmark className="text-sm" /></button>
        </div>
        <div className="p-5 text-center">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: "rgba(37, 211, 102, 0.12)", border: "1px solid rgba(37, 211, 102, 0.2)" }}><FaWhatsapp className="text-2xl text-green-400" /></div>
          <p className="text-gray-200 font-medium mb-1">Kirim {count} invoice?</p>
          <p className="text-xs text-slate-500">Invoice akan dikirim via WhatsApp ke masing-masing pelanggan</p>
        </div>
        <div className="px-5 py-4 flex gap-3" style={{ borderTop: "1px solid rgba(51, 65, 85, 0.3)" }}>
          <button onClick={onClose} disabled={loading} className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-slate-300 transition-all disabled:opacity-50" style={{ background: "rgba(51, 65, 85, 0.3)", border: "1px solid rgba(51, 65, 85, 0.3)" }}>Batal</button>
          <button onClick={onConfirm} disabled={loading} className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-white bg-green-600 hover:bg-green-500 transition-all disabled:opacity-50 flex items-center justify-center gap-2">
            {loading ? (<><FaSpinner className="animate-spin text-xs" />Mengirim...</>) : (<><FaPaperPlane className="text-xs" />Kirim</>)}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ============================================
   Modal Hasil Pengiriman
   ============================================ */
const ModalSendResult = ({ open, onClose, results }) => {
  if (!open || !results) return null;
  const { total, success, failed, details } = results;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="relative w-full max-w-md rounded-2xl overflow-hidden shadow-2xl" style={{ background: "rgba(15, 23, 42, 0.95)", border: "1px solid rgba(51, 65, 85, 0.4)" }} onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: "1px solid rgba(51, 65, 85, 0.3)" }}>
          <h3 className="text-sm font-bold text-gray-100">Hasil Pengiriman</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-gray-100 hover:bg-slate-700/50 transition-all"><FaXmark className="text-sm" /></button>
        </div>
        <div className="px-5 py-4 grid grid-cols-3 gap-3" style={{ borderBottom: "1px solid rgba(51, 65, 85, 0.2)" }}>
          <div className="text-center"><p className="text-xl font-extrabold text-gray-100">{total}</p><p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mt-0.5">Total</p></div>
          <div className="text-center"><p className="text-xl font-extrabold text-emerald-400">{success}</p><p className="text-[10px] font-semibold text-emerald-500/60 uppercase tracking-wider mt-0.5">Berhasil</p></div>
          <div className="text-center"><p className="text-xl font-extrabold text-red-400">{failed}</p><p className="text-[10px] font-semibold text-red-500/60 uppercase tracking-wider mt-0.5">Gagal</p></div>
        </div>
        <div className="max-h-[40vh] overflow-y-auto">
          {details?.map((item, idx) => (
            <div key={idx} className={`px-5 py-3 flex items-start gap-3 ${idx !== details.length - 1 ? "border-b" : ""}`} style={{ borderColor: "rgba(51, 65, 85, 0.12)" }}>
              {item.success ? <FaCheckCircle className="text-emerald-400 text-sm mt-0.5 flex-shrink-0" /> : <FaTimesCircle className="text-red-400 text-sm mt-0.5 flex-shrink-0" />}
              <div className="min-w-0 flex-1"><p className="text-sm font-medium text-gray-200 truncate">{item.user}</p><p className="text-xs text-slate-500 mt-0.5 truncate">{item.message}</p></div>
            </div>
          ))}
        </div>
        <div className="px-5 py-4" style={{ borderTop: "1px solid rgba(51, 65, 85, 0.3)" }}>
          <button onClick={onClose} className="w-full px-4 py-2.5 rounded-xl text-sm font-medium text-gray-100 bg-cyan-600 hover:bg-cyan-500 transition-all">Tutup</button>
        </div>
      </div>
    </div>
  );
};

/* ============================================
   Drawer Detail Invoice
   ============================================ */
const DrawerDetail = ({ open, onClose, invoiceId, onPreview, onSendSingle, sendingId, onPay, onPromise }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !invoiceId) {
      setDetail(null);
      return;
    }
    setLoading(true);
    InvoiceService.show({ id: invoiceId })
      .then((res) => setDetail(res.data?.data || res.data || null))
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  }, [open, invoiceId]);

  if (!open) return null;

  const pppoeUser = detail?.pppoe_user;
  const isActive = detail?.status === "lunas" || detail?.status === "paid";
  const isUnpaid = !isActive;

  return (
    <>
      <div className={`fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`} onClick={onClose} />
      <div className={`fixed top-0 right-0 z-50 h-full w-full max-w-md shadow-2xl transition-transform duration-300 ease-out ${open ? "translate-x-0" : "translate-x-full"}`} style={{ background: "rgba(15, 23, 42, 0.97)", borderLeft: "1px solid rgba(51, 65, 85, 0.4)" }}>
        <div className="px-5 py-4 flex items-center justify-between sticky top-0 z-10" style={{ background: "rgba(15, 23, 42, 0.97)", borderBottom: "1px solid rgba(51, 65, 85, 0.3)" }}>
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "rgba(0, 180, 216, 0.12)", border: "1px solid rgba(0, 180, 216, 0.2)" }}><FaFileInvoiceDollar className="text-sm text-cyan-400" /></div>
            <h3 className="text-sm font-bold text-gray-100">Detail Invoice</h3>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-gray-100 hover:bg-slate-700/50 transition-all"><FaXmark className="text-sm" /></button>
        </div>

        <div className="overflow-y-auto p-5 space-y-5" style={{ height: "calc(100% - 65px)" }}>
          {loading ? (
            <div className="space-y-4 animate-pulse">{[...Array(6)].map((_, i) => <div key={i} className="h-4 bg-slate-700/40 rounded" style={{ width: `${50 + Math.random() * 40}%` }} />)}</div>
          ) : detail ? (
            <>
              <div className="flex items-center justify-between">
                <span className="font-mono text-xs text-cyan-400/80 bg-cyan-500/10 px-3 py-1.5 rounded-lg border border-cyan-500/15">{detail.invoice_number || "-"}</span>
                <StatusBadge status={detail.status} />
              </div>

              <div className="rounded-xl p-4 space-y-3" style={{ background: "rgba(51, 65, 85, 0.1)", border: "1px solid rgba(51, 65, 85, 0.2)" }}>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Pelanggan</p>
                <InfoRow label="Nama" value={pppoeUser?.name || "-"} icon={<FaUser className="text-xs text-slate-500" />} />
                <InfoRow label="No. Internet" value={pppoeUser?.internet_number || "-"} icon={<FaHashtag className="text-xs text-slate-500" />} />
                <InfoRow label="No. HP" value={pppoeUser?.phone_number || "-"} />
                <InfoRow label="Paket" value={pppoeUser?.profile?.name || "-"} />
              </div>

              <div className="rounded-xl p-4 space-y-3" style={{ background: "rgba(51, 65, 85, 0.1)", border: "1px solid rgba(51, 65, 85, 0.2)" }}>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Tagihan</p>
                <InfoRow label="Periode" value={detail.periode || detail.start_periode || "-"} />
                <InfoRow label="Jatuh Tempo" value={detail.due_date || "-"} />
                <InfoRow label="Pop" value={detail.pop?.name || "-"} />
                <InfoRow label="Odc" value={detail.odc?.name || "-"} />
                <InfoRow label="Odp" value={detail.opd?.name || "-"} />
              </div>

              <div className="rounded-xl p-4 text-center" style={{ background: isActive ? "rgba(16, 185, 129, 0.06)" : "rgba(0, 180, 216, 0.06)", border: `1px solid ${isActive ? "rgba(16, 185, 129, 0.15)" : "rgba(0, 180, 216, 0.15)"}` }}>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Total Tagihan</p>
                <p className={`text-2xl font-extrabold ${isActive ? "text-emerald-400" : "text-cyan-400"}`}>Rp. {formatRupiah(detail.nominal ?? 0)}</p>
              </div>

              {/* ACTION BUTTONS */}
              <div className="flex flex-col gap-3 pt-2">
                {isUnpaid && (
                    <button
                    onClick={() => onPay(detail)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-500 transition-all shadow-lg shadow-indigo-600/20"
                    >
                        <FaMoneyBill className="text-sm" /> Bayar Sekarang
                    </button>
                )}
                
                <div className="flex gap-3">
                  <button
                    onClick={() => onPreview(invoiceId)}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium text-cyan-400 transition-all hover:bg-cyan-500/10"
                    style={{ border: "1px solid rgba(0, 180, 216, 0.25)" }}
                  >
                    <FaEye className="text-xs" /> Preview
                  </button>
                  <button
                    onClick={() => onSendSingle(invoiceId)}
                    disabled={sendingId === invoiceId}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium text-white bg-green-600 hover:bg-green-500 transition-all disabled:opacity-40"
                  >
                    {sendingId === invoiceId ? <FaSpinner className="animate-spin text-xs" /> : <FaWhatsapp className="text-sm" />}
                    Kirim WA
                  </button>
                </div>
                {isUnpaid && (
                    <button
                    onClick={() => onPromise(detail)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium text-amber-400 bg-amber-500/10 border border-amber-500/20 hover:bg-amber-500/20 transition-all"
                    >
                        <FaHandshake className="text-sm" /> Janji Bayar
                    </button>
                )}
              </div>
            </>
          ) : (
            <div className="text-center py-12"><p className="text-slate-500">Gagal memuat detail</p></div>
          )}
        </div>
      </div>
    </>
  );
};

const InfoRow = ({ label, value, icon }) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-2">{icon}<span className="text-xs text-slate-500">{label}</span></div>
    <span className="text-sm font-medium text-gray-200 text-right">{value}</span>
  </div>
);

/* ============================================
   Row Actions — UPDATE
   ============================================ */
const RowActions = ({ invoice, onPreview, onSendSingle, sendingId, onOpenDetail, onPay, onPromise }) => {
  const isSending = sendingId === invoice.id;
  const isUnpaid = invoice.status !== "lunas" && invoice.status !== "paid";
  
  return (
    <div className="flex items-center gap-0.5">
      <button onClick={(e) => { e.stopPropagation(); onOpenDetail(); }} className="p-2 rounded-lg text-slate-400 hover:text-gray-100 hover:bg-slate-700/40 transition-all" title="Detail">
        <FaFileInvoiceDollar className="text-[11px]" />
      </button>
      
      {/* Tombol Bayar (Icon) - Hanya tampil jika belum lunas */}
      {isUnpaid && (
        <button onClick={(e) => { e.stopPropagation(); onPay(invoice); }} className="p-2 rounded-lg text-slate-400 hover:text-indigo-400 hover:bg-indigo-500/10 transition-all" title="Bayar">
            <FaMoneyBill className="text-[11px]" />
        </button>
      )}

      {/* Tombol Janji Bayar (Icon) - Hanya tampil jika belum lunas */}
      {isUnpaid && (
        <button onClick={(e) => { e.stopPropagation(); onPromise(invoice); }} className="p-2 rounded-lg text-slate-400 hover:text-amber-400 hover:bg-amber-500/10 transition-all" title="Janji Bayar">
            <FaHandshake className="text-[11px]" />
        </button>
      )}

      <button onClick={(e) => { e.stopPropagation(); onPreview(invoice.id); }} className="p-2 rounded-lg text-slate-400 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all" title="Preview pesan">
        <FaEye className="text-[11px]" />
      </button>
      <button onClick={(e) => { e.stopPropagation(); onSendSingle(invoice.id); }} disabled={isSending} className="p-2 rounded-lg text-slate-400 hover:text-green-400 hover:bg-green-500/10 transition-all disabled:opacity-40" title="Kirim WA">
        {isSending ? <FaSpinner className="text-[11px] animate-spin" /> : <FaWhatsapp className="text-[11px]" />}
      </button>
    </div>
  );
};

/* ============================================
   KOMPONEN UTAMA
   ============================================ */
export const Invoice = () => {
  const [showSidebar, setShowSidebar] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);       // invoice IDs
  const [showInvoiceCreate, setShowInvoiceCreate] = useState(false);

  const [sending, setSending] = useState(false);
  const [sendingId, setSendingId] = useState(null);         // invoice ID yang sedang kirim
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [sendResults, setSendResults] = useState(null);
  const [showSendResult, setShowSendResult] = useState(false);

  const [showInvoicePayment, setShowInvoicePayment] = useState(false);
  const [paymentData, setPaymentData] = useState(null);

  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerInvoiceId, setDrawerInvoiceId] = useState(null);

  // State untuk Janji Bayar
  const [showPromiseModal, setShowPromiseModal] = useState(false);
  const [promiseUserData, setPromiseUserData] = useState(null);
  const [selectedPppoeUserId, setSelectedPppoeUserId] = useState(null);

  const [toast, setToast] = useState(null);

  const { data, meta, loading, params, setParams, refreshData } = useHookInvoice();

  const showToast = useCallback((message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  /* --- Selection pakai invoice ID --- */
  const allIds = useMemo(() => (data ? data.map((i) => i.id) : []), [data]);
  const isAllSelected = useMemo(() => allIds.length > 0 && selectedIds.length === allIds.length, [allIds, selectedIds]);

  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => (prev.length === allIds.length ? [] : [...allIds]));
  }, [allIds]);

  /* --- Drawer --- */
  const openDetail = useCallback((id) => { setDrawerInvoiceId(id); setDrawerOpen(true); }, []);
  const closeDetail = useCallback(() => { setDrawerOpen(false); setTimeout(() => setDrawerInvoiceId(null), 300); }, []);

  /* --- PAYMENT HANDLER --- */
  const handlePay = useCallback((invoice) => {
    setPaymentData(invoice);
    setShowInvoicePayment(true);
  }, []);

  /* --- PROMISE TO PAY HANDLER --- */
  const handlePromise = useCallback((invoice) => {
    setPromiseUserData(invoice.pppoe_user);
    setSelectedPppoeUserId(invoice.pppoe_user.id);
    setShowPromiseModal(true);
  }, []);

  /* --- Preview (pakai invoice ID) --- */
  const handlePreview = async (invoiceId) => {
    setPreviewLoading(true);
    setPreviewData(null);
    setShowPreview(true);
    try {
      const res = await InvoiceService.preview({ invoiceId });
      if (res.data?.status === "success") {
        setPreviewData(res.data.data);
      } else {
        showToast(res.data?.message_v2 || "Gagal memuat preview", "error");
        setShowPreview(false);
      }
    } catch (err) {
      showToast(err.response?.data?.message_v2 || "Gagal memuat preview", "error");
      setShowPreview(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  /* --- Send Single (pakai invoice ID) --- */
  const handleSendSingle = async (invoiceId) => {
    setSendingId(invoiceId);
    try {
      const res = await InvoiceService.sendSingle({ invoiceId });
      if (res.data?.status === "success") {
        showToast(res.data.message_v2);
        refreshData();
      } else {
        showToast(res.data?.message_v2 || "Gagal mengirim", "error");
      }
    } catch (err) {
      showToast(err.response?.data?.message_v2 || "Gagal mengirim invoice", "error");
    } finally {
      setSendingId(null);
    }
  };

  /* --- Send Batch (pakai invoice ID) --- */
  const handleSendBatch = async () => {
    setSending(true);
    try {
      const res = await InvoiceService.sendBatch({ data: selectedIds });
      setSendResults(res.data?.data);
      setShowSendResult(true);
      setShowSendConfirm(false);
      setSelectedIds([]);
      refreshData();
    } catch (err) {
      showToast(err.response?.data?.message_v2 || "Gagal mengirim invoice", "error");
    } finally {
      setSending(false);
    }
  };

  /* --- Pagination --- */
  const getPages = () => {
    if (!meta) return [];
    const { last_page: total, current_page: current } = meta;
    const delta = 2;
    const pages = [];
    const left = Math.max(2, current - delta);
    const right = Math.min(total - 1, current + delta);
    pages.push(1);
    if (left > 2) pages.push("...");
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < total - 1) pages.push("...");
    if (total > 1) pages.push(total);
    return pages;
  };

  const columns = [
    { key: "no", label: "No", align: "center", width: "55px" },
    { key: "status", label: "Status", align: "left" },
    { key: "invoice_number", label: "Kode Invoice", align: "left" },
    { key: "internet_number", label: "No Internet", align: "left" },
    { key: "name", label: "Nama", align: "left" },
    { key: "nominal", label: "Total Tagihan", align: "left" },
    { key: "nominal_bayar", label: "Total Bayar", align: "left" },
    { key: "sisa_tagihan", label: "Sisa Tagihan", align: "left" },
    { key: "pop", label: "Pop", align: "left" },
    { key: "odc", label: "Odc", align: "left" },
    { key: "opd", label: "Odp", align: "left" },
    { key: "actions", label: "Aksi", align: "center", width: "140px" }, // Perlebar sedikit untuk tombol bayar
  ];

  return (
    <>
      <Helmet><title>Invoice - DANELA NET</title></Helmet>

      <DashboardLayout showSidebar={showSidebar} setShowSidebar={setShowSidebar}>
        <Header toggleSidebar={() => setShowSidebar(!showSidebar)}>
          <div className="flex items-center gap-3">
            <h2 className="text-base md:text-xl font-bold text-gray-100">Invoice Tagihan</h2>
            {meta && <span className="hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">{meta.total} data</span>}
          </div>
          <div className="flex items-center gap-2">
            {selectedIds.length > 0 && (
              <button onClick={() => setShowSendConfirm(true)} disabled={sending} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-green-600 hover:bg-green-500 transition-all disabled:opacity-50 shadow-lg shadow-green-600/20">
                {sending ? <FaSpinner className="animate-spin text-xs" /> : <FaWhatsapp className="text-sm" />}
                <span className="hidden sm:inline">Kirim</span>
                <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-md bg-green-500/30 text-[11px] font-bold">{selectedIds.length}</span>
              </button>
            )}
            <BtnDefault onClick={() => setShowInvoiceCreate(true)}><FaPlus className="text-xs hidden sm:block" /><span>Tambah Data</span></BtnDefault>
          </div>
        </Header>

        <main className="p-4 lg:p-6 space-y-5">
          {/* Search */}
          <section className="rounded-2xl p-4 sm:p-5" style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)", backdropFilter: "blur(12px)" }}>
            <div className="flex flex-col lg:flex-row lg:items-end gap-4">
              <div className="w-full lg:max-w-xs flex-shrink-0">
                <label htmlFor="search-invoice" className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">Cari Data</label>
                <div className="relative">
                  <FaMagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm" />
                  <input id="search-invoice" type="text" placeholder="Nama, ID Pelanggan..." value={params?.search || ""} onChange={(e) => setParams((prev) => ({ ...prev, search: e.target.value, page: 1 }))} className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 placeholder-slate-500 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all" />
                </div>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1.5"><FaFilter className="text-xs text-slate-500" /><span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Filter</span></div>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">{/* Filter components */}</div>
              </div>
            </div>
          </section>

          {/* ===== DESKTOP TABLE ===== */}
          <section className="hidden lg:block rounded-2xl overflow-hidden" style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}>
            <div className="overflow-x-auto">
              <table className="min-w-max max-w-screen-2xl text-sm">
                <thead>
                  <tr className="border-b" style={{ background: "rgba(15, 23, 42, 0.7)", borderColor: "rgba(51, 65, 85, 0.3)" }}>
                    <th className="px-4 py-3.5 text-center w-11">
                      <input type="checkbox" checked={isAllSelected} onChange={toggleSelectAll} disabled={allIds.length === 0} className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500/30 focus:ring-offset-0 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed" />
                    </th>
                    {columns.map((col) => (
                      <th key={col.key} className={`px-4 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-400 ${col.align === "center" ? "text-center" : "text-left"}`} style={col.width ? { width: col.width } : undefined}>{col.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: "rgba(51, 65, 85, 0.15)" }}>
                  {loading && !data && [...Array(5)].map((_, i) => <SkeletonRow key={i} cols={columns.length + 1} />)}
                  {!loading && data?.length === 0 && (
                    <tr><td colSpan={columns.length + 1} className="px-4 py-16 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: "rgba(51, 65, 85, 0.2)", border: "1px solid rgba(51, 65, 85, 0.2)" }}><FaFileInvoiceDollar className="text-2xl text-slate-600" /></div>
                        <p className="text-slate-400 font-medium">Tidak ada invoice ditemukan</p>
                      </div>
                    </td></tr>
                  )}
                  {!loading && data?.map((item, index) => {
                    const checked = selectedIds.includes(item.id);
                    const isUnpaid = item.status !== "lunas" && item.status !== "paid";
                    return (
                      <tr
                        key={item.id}
                        className={`cursor-pointer transition-colors duration-150 ${checked ? "bg-cyan-500/[0.04]" : ""}`} onMouseEnter={(e) => { if (!checked) e.currentTarget.style.background = "rgba(0, 180, 216, 0.04)"; }} onMouseLeave={(e) => { if (!checked) e.currentTarget.style.background = "transparent"; }} >
                        <td className="px-4 py-3 text-center">
                          <input type="checkbox" checked={checked} onChange={() => toggleSelect(item.id)} onClick={(e) => e.stopPropagation()} className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500/30 focus:ring-offset-0 cursor-pointer" />
                        </td>
                        <td className="px-4 py-3 text-center text-slate-500 font-mono text-xs">{meta.from + index}</td>
                        
                        {/* STATUS: Tidak ada tombol lagi, hanya badge */}
                        <td className="px-4 py-3 min-w-[130px]">
                            <StatusBadge status={item?.status} />
                        </td>

                        <td className="px-4 py-3"><span className="font-mono text-xs text-cyan-400/80 bg-cyan-500/8 px-2 py-0.5 rounded">{item?.invoice_number || "-"}</span></td>
                        <td className="px-4 py-3"><span className="font-mono text-xs text-cyan-400/80 bg-cyan-500/8 px-2 py-0.5 rounded">{item?.pppoe_user?.internet_number || "-"}</span></td>
                        <td className="px-4 py-3 min-w-[200px]"><div className="flex items-center gap-2"><FaUser className="text-xs text-slate-500" /><span className="font-semibold text-gray-100">{item?.pppoe_user?.name || "-"}</span></div></td>
                        <td className="px-4 py-3 min-w-[140px]"><span className="font-bold text-cyan-400">Rp. {formatRupiah(item?.nominal ?? 0)}</span></td>
                        <td className="px-4 py-3 min-w-[140px]"><span className="font-bold text-cyan-400">Rp. {formatRupiah(item?.total_payment ?? 0)}</span></td>
                        <td className="px-4 py-3 min-w-[140px]"><span className="font-bold text-cyan-400">Rp. {formatRupiah(item?.nominal - item?.total_payment ?? 0)}</span></td>
                        <td className="px-4 py-3">{item?.pop?.name || "-"}</td>
                        <td className="px-4 py-3">{item?.odc?.name || "-"}</td>
                        <td className="px-4 py-3">{item?.opd?.name || "-"}</td>
                        <td className="px-4 py-3 text-center">
                          <RowActions 
                            invoice={item} 
                            onPreview={handlePreview} 
                            onSendSingle={handleSendSingle} 
                            sendingId={sendingId} 
                            onOpenDetail={() => openDetail(item.id)} 
                            onPay={handlePay}
                            onPromise={handlePromise}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {meta && meta.last_page > 1 && (
              <div className="flex items-center justify-between px-5 py-3.5" style={{ borderTop: "1px solid rgba(51, 65, 85, 0.2)" }}>
                <p className="text-xs text-slate-500">Menampilkan <span className="text-slate-300 font-medium">{meta.from}</span> sampai <span className="text-slate-300 font-medium">{meta.to}</span> dari <span className="text-slate-300 font-medium">{meta.total}</span></p>
                <nav className="flex items-center gap-1">
                  <button onClick={() => setParams((p) => ({ ...p, page: 1 }))} disabled={meta.current_page === 1} className="p-2 rounded-lg text-slate-400 hover:text-cyan-400 hover:bg-slate-700/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all"><FaAnglesLeft className="text-xs" /></button>
                  <button onClick={() => setParams((p) => ({ ...p, page: meta.current_page - 1 }))} disabled={meta.current_page === 1} className="p-2 rounded-lg text-slate-400 hover:text-cyan-400 hover:bg-slate-700/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all"><FaChevronLeft className="text-xs" /></button>
                  {getPages().map((p, i) => p === "..." ? <span key={`d-${i}`} className="px-2 text-slate-600 text-sm">···</span> : (
                    <button key={p} onClick={() => setParams((prev) => ({ ...prev, page: p }))} className={`min-w-[34px] h-9 px-2 rounded-lg text-sm font-medium transition-all duration-200 ${meta.current_page === p ? "bg-cyan-600 text-white shadow-lg shadow-cyan-600/25" : "text-slate-400 hover:text-gray-100 hover:bg-slate-700/40"}`}>{p}</button>
                  ))}
                  <button onClick={() => setParams((p) => ({ ...p, page: meta.current_page + 1 }))} disabled={meta.current_page === meta.last_page} className="p-2 rounded-lg text-slate-400 hover:text-cyan-400 hover:bg-slate-700/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all"><FaChevronRight className="text-xs" /></button>
                  <button onClick={() => setParams((p) => ({ ...p, page: meta.last_page }))} disabled={meta.current_page === meta.last_page} className="p-2 rounded-lg text-slate-400 hover:text-cyan-400 hover:bg-slate-700/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all"><FaAnglesRight className="text-xs" /></button>
                </nav>
              </div>
            )}
          </section>

          {/* ===== MOBILE CARDS ===== */}
          <section className="lg:hidden space-y-3">
            {loading && !data && [...Array(4)].map((_, i) => <SkeletonCard key={i} />)}
            {!loading && data?.length === 0 && (
              <div className="rounded-2xl p-12 text-center" style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}>
                <p className="text-slate-400 font-medium">Tidak ada invoice ditemukan</p>
              </div>
            )}
            {!loading && data?.map((item) => {
              const checked = selectedIds.includes(item.id);
              const isUnpaid = item.status !== "lunas" && item.status !== "paid";
              return (
                <article key={item.id} onClick={() => openDetail(item.id)} className={`rounded-2xl overflow-hidden active:scale-[0.98] transition-all duration-200 cursor-pointer ${checked ? "ring-1 ring-cyan-500/30" : ""}`} style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}>
                  <div className="px-4 py-3 flex justify-between items-center" style={{ background: "rgba(15, 23, 42, 0.7)", borderBottom: "1px solid rgba(51, 65, 85, 0.2)" }}>
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 rounded-lg" style={{ background: "rgba(0, 180, 216, 0.1)", border: "1px solid rgba(0, 180, 216, 0.15)" }}><FaHashtag className="text-xs text-cyan-400" /></div>
                      <span className="text-xs font-bold text-slate-300 font-mono">{item?.pppoe_user?.name || "-"}</span>
                    </div>
                    <StatusBadge status={item?.status} />
                  </div>
                  <div className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div className="space-y-1 flex-1">
                        <h3 className="font-semibold text-gray-100 leading-tight text-sm">{item?.invoice_number || "-"}</h3>
                        <div className="flex items-center gap-1.5 text-slate-500"><FaCreditCard className="text-[11px]" /><span className="text-xs font-medium">Tagihan Bulan Ini</span></div>
                      </div>
                      <div className="text-right ml-4">
                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider leading-none mb-1.5">Total</p>
                        <p className="text-lg font-extrabold text-cyan-400">{formatRupiah(item?.nominal ?? 0)}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-3" style={{ borderTop: "1px solid rgba(51, 65, 85, 0.15)" }}>
                      <div className="flex items-center gap-1.5">
                        <input type="checkbox" checked={checked} onChange={() => toggleSelect(item.id)} onClick={(e) => e.stopPropagation()} className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500/30 focus:ring-offset-0 cursor-pointer" />
                        
                        {/* AKSI MOBILE */}
                        <button onClick={(e) => { e.stopPropagation(); handlePreview(item.id); }} className="p-1.5 rounded text-slate-400 hover:text-cyan-400 transition-all"><FaEye className="text-xs" /></button>
                        
                        {/* Tombol Bayar Mobile */}
                        {isUnpaid && (
                             <button onClick={(e) => { e.stopPropagation(); handlePay(item); }} className="p-1.5 rounded text-slate-400 hover:text-indigo-400 transition-all"><FaMoneyBill className="text-xs" /></button>
                        )}

                        {/* Tombol Janji Bayar Mobile */}
                        {isUnpaid && (
                             <button onClick={(e) => { e.stopPropagation(); handlePromise(item); }} className="p-1.5 rounded text-slate-400 hover:text-amber-400 transition-all"><FaHandshake className="text-xs" /></button>
                        )}

                        <button onClick={(e) => { e.stopPropagation(); handleSendSingle(item.id); }} disabled={sendingId === item.id} className="p-1.5 rounded text-slate-400 hover:text-green-400 transition-all disabled:opacity-40">
                          {sendingId === item.id ? <FaSpinner className="text-xs animate-spin" /> : <FaWhatsapp className="text-xs" />}
                        </button>
                      </div>
                      <div className="flex items-center gap-1 text-cyan-400 font-semibold text-xs">Detail <FaChevronRight className="text-[10px]" /></div>
                    </div>
                  </div>
                </article>
              );
            })}
            {meta && meta.last_page > 1 && (
              <div className="flex items-center justify-between pt-3 pb-6 px-1" style={{ borderTop: "1px solid rgba(51, 65, 85, 0.15)" }}>
                <button onClick={() => setParams((p) => ({ ...p, page: meta.current_page - 1 }))} disabled={meta.current_page === 1} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-slate-300 disabled:opacity-30" style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}><FaChevronLeft className="text-xs" /> Sebelumnya</button>
                <span className="text-sm font-medium text-slate-400"><span className="text-cyan-400">{meta.current_page}</span><span className="text-slate-600 mx-1">/</span>{meta.last_page}</span>
                <button onClick={() => setParams((p) => ({ ...p, page: meta.current_page + 1 }))} disabled={meta.current_page === meta.last_page} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-slate-300 disabled:opacity-30" style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}>Berikutnya <FaChevronRight className="text-xs" /></button>
              </div>
            )}
          </section>
        </main>
      </DashboardLayout>

      {/* MODALS */}
      <ModalInvoiceCreate openModal={showInvoiceCreate} setOpenModal={setShowInvoiceCreate} refresh={refreshData} />
      <ModalInvoicePayment openModal={showInvoicePayment} setOpenModal={setShowInvoicePayment} refresh={refreshData} data={paymentData} />
      <CreateNewPromiseToPayModal 
        openModal={showPromiseModal} 
        setOpenModal={setShowPromiseModal} 
        refresh={refreshData} 
        hidePppoeUser={true}
        pppoeUserId={selectedPppoeUserId}
      />

      <ModalPreview open={showPreview} onClose={() => setShowPreview(false)} data={previewData} loading={previewLoading} />
      <ModalSendConfirm open={showSendConfirm} onClose={() => setShowSendConfirm(false)} onConfirm={handleSendBatch} loading={sending} count={selectedIds.length} />
      <ModalSendResult open={showSendResult} onClose={() => setShowSendResult(false)} results={sendResults} />
      <DrawerDetail open={drawerOpen} onClose={closeDetail} invoiceId={drawerInvoiceId} onPreview={handlePreview} onSendSingle={handleSendSingle} onPay={handlePay} onPromise={handlePromise} sendingId={sendingId} />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </>
  );
};